import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PublicPlayer } from '@sincronia/shared';
import { prefersReducedMotion } from '../../lib/feedback.js';
import { avatarColor, cn } from '../../lib/ui.js';
import type { Flash } from '../../store/gameStore.js';

/** Tempo que o nome de quem jogou fica na tela. */
export const PLAY_FLASH_MS = 1500;

type Shown = { name: string; color: string; key: number };

/**
 * Anuncia, logo abaixo da pilha, quem acabou de soltar uma carta.
 *
 * Só o nome: o número já está visível na carta do centro. Não bloqueia
 * interação (`pointer-events-none`) e, com `prefers-reduced-motion`, aparece e
 * some sem animação.
 */
export function PlayFlash({
  flash,
  players,
}: {
  flash: Flash | null;
  players: PublicPlayer[];
}): ReactNode {
  const [shown, setShown] = useState<Shown | null>(null);
  const [reduced] = useState(() => prefersReducedMotion());
  const playersRef = useRef(players);
  const timerRef = useRef(0);
  playersRef.current = players;

  useEffect(() => {
    if (!flash || flash.kind !== 'accepted') return;
    const player = playersRef.current.find((item) => item.id === flash.payload.playerId);

    window.clearTimeout(timerRef.current);
    setShown({
      name: player?.name ?? '—',
      color: player ? avatarColor(player.avatarKey) : 'var(--color-pulse-400)',
      key: flash.at,
    });
    timerRef.current = window.setTimeout(() => setShown(null), PLAY_FLASH_MS);
  }, [flash]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  if (!shown) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center px-4"
      aria-live="polite"
      data-testid="play-flash"
    >
      <span
        key={shown.key}
        className={cn(
          'max-w-[90vw] truncate text-4xl leading-none font-bold drop-shadow-[0_6px_28px_rgba(0,0,0,0.95)] sm:text-5xl',
          !reduced && 'animate-[name-flash_1500ms_ease-out_forwards]',
        )}
        style={{ color: shown.color }}
        data-testid="play-flash-name"
      >
        {shown.name}
      </span>
    </div>
  );
}
